import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { brl, stageColor } from "@/lib/format";
import { Search, Building2, KanbanSquare, CalendarClock, Loader2, CornerDownLeft } from "@/components/icons";

const Group = ({ title, icon: Icon, children }) => (
  <div className="mb-2">
    <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="w-3.5 h-3.5" /> {title}
    </div>
    {children}
  </div>
);

const Item = ({ onClick, title, sub, right, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-brand-soft/60 text-left transition-colors">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-navy truncate">{title}</div>
      {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
    </div>
    {right}
  </button>
);

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQ(""); setData(null);
    }
  }, [open]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setData(null); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try { const r = await api.get("/busca", { params: { q: q.trim() } }); setData(r.data); }
      catch { setData(null); } finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const go = useCallback((path) => { onOpenChange(false); navigate(path); }, [navigate, onOpenChange]);

  const firstResult = () => {
    if (!data) return null;
    if (data.clientes?.[0]) return `/cliente/${data.clientes[0].id}`;
    if (data.oportunidades?.[0]) return `/deal/${data.oportunidades[0].id}`;
    return null;
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      const p = firstResult();
      if (p) go(p);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-xl top-[15%] translate-y-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Busca rápida</DialogTitle>
        <div className="flex items-center gap-3 px-4 h-14 border-b border-black/5">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Buscar clientes, oportunidades, atividades..." data-testid="command-input"
            className="flex-1 h-full bg-transparent outline-none text-sm text-navy placeholder:text-slate-400" />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-brand" />}
          <kbd className="hidden sm:inline text-[10px] font-medium text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">ESC</kbd>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-2">
          {q.trim().length < 2 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              Digite ao menos 2 caracteres para buscar.
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400"><CornerDownLeft className="w-3.5 h-3.5" /> Enter abre o primeiro resultado</div>
            </div>
          )}
          {data && data.total === 0 && !loading && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">{`Nenhum resultado para "${q}".`}</div>
          )}
          {data && data.clientes?.length > 0 && (
            <Group title="Clientes" icon={Building2}>
              {data.clientes.map((c) => (
                <Item key={c.id} testId={`cmd-cliente-${c.id}`} onClick={() => go(`/cliente/${c.id}`)} title={c.nome} sub={c.empresa || c.telefone || c.email} />
              ))}
            </Group>
          )}
          {data && data.oportunidades?.length > 0 && (
            <Group title="Oportunidades" icon={KanbanSquare}>
              {data.oportunidades.map((d) => (
                <Item key={d.id} testId={`cmd-deal-${d.id}`} onClick={() => go(`/deal/${d.id}`)} title={d.titulo} sub={`${d.cliente_nome || "Sem cliente"} · ${brl(d.valor)}`}
                  right={<span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${stageColor(d.stage)}`}>{d.stage}</span>} />
              ))}
            </Group>
          )}
          {data && data.atividades?.length > 0 && (
            <Group title="Atividades" icon={CalendarClock}>
              {data.atividades.map((a) => (
                <Item key={a.id} testId={`cmd-ativ-${a.id}`} onClick={() => go(a.deal_id ? `/deal/${a.deal_id}` : a.cliente_id ? `/cliente/${a.cliente_id}` : "/atividades")} title={a.titulo} sub={a.tipo} />
              ))}
            </Group>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
